import * as jsCookie from 'js-cookie'
import Cookie from 'shared/helpers/cookie'

import {
  is,
  all,
  has,
  map,
  path,
  both,
  prop,
  isNil,
  props,
  pluck,
  propOr,
  pathOr,
  allPass,
  compose,
  anyPass,
  isEmpty,
  invoker,
  unapply,
  contains,
  complement,
  partialRight,
  pipe,
} from 'ramda'



import Storage from 'shared/helpers/storage'
import {parseJwt} from 'shared/helpers/functional'
import * as app from './app'
import moment from 'moment'
import { getLangCodeFromIsoCode, getLangByIsoCode } from  '../../assets/localization/pure'

const environment = app.environment()

const { goTo, goToRedirectLocation } = app
const { storageKeys } = environment

const rtlLanguages = new Set(['ar'])

const roles = environment.userRoles
const validProp = both(complement(isNil), complement(isEmpty))
const _getRoles = compose(
  map(invoker(0, 'toUpperCase')),
  pluck('name'),
  propOr([], 'roles')
)
const hasRole = (user, roleName) => contains(
  roleName.toUpperCase(),
  _getRoles(user)
)
const isRole = unapply(partialRight(hasRole))
const validRoles = allPass([
  validProp,
  is(Array),
  all(both(is(Object), has('name'))),
])

/**
 * exports
 */

export const canLogin = compose(validRoles, prop('roles'))
export const isAuthorized = both(
  canLogin,
  compose(all(validProp), props(['id', 'accessToken'])),
)
export const isDisabled = propOr(false, 'optedOut')
export const usesInternalReferralProgram = pipe(path(['internalReferralProgramEnabledAt']), Boolean)
export const rewardsUnpaid = pathOr(0, ['rewards', 'totalUnpaid'])

// roles
export const getRoles = _getRoles
export const isAdmin = isRole(roles.ADMIN)
export const isResponder = isRole(roles.RESPONDER)
export const isSuperAdmin = isRole(roles.SUPER_ADMIN)
export const isCallCenterUser = isRole(roles.CALL_CENTER)
export const isAnyAdmin = anyPass([isAdmin, isSuperAdmin])

export const getLocale = compose(
  getLangCodeFromIsoCode,
  pathOr('en_us', ['localeLanguage', 'isoCode']),
)

// TODO - should probably get this from API ?
export const isType = (user, provider) => {
  const typeId = user.userTypeId

  switch (provider.toLowerCase()) {
    case 'incrowd':
      return typeId === 1
    case 'employee':
      return typeId === 13
    default:
      return false
  }
}

export const shouldShowTerms = user => (
  isType(user, 'incrowd') &&
  !user.acceptedTerms
)

// 1, 8, and 13 are type ids that are part of the system already
export const isThirdParty = user => !(new Set([1, 8, 13]).has(user.responderTypeId))

export const shouldShowRtl = user => rtlLanguages.has(getLocale(user))

// FIXME - why are there two potential redirects happening in some cases?
export const goToHomepage = user => {
  if (user.mustChangePassword) {
    goTo(`${environment.urls.login}change-password`)
  } else if (isAnyAdmin(user)) {
    if (!goToRedirectLocation()) goTo(`${environment.urls.admin}`)
  } else if (isResponder(user)) {
    if (!goToRedirectLocation()) goTo(`${environment.urls.responder}`)
  } else if (isCallCenterUser(user)) {
    goTo(`${environment.urls.register}`)
  } else {
    throw new Error('User has no roles')
  }
}

export const isRestrictedFromPayment = user => {
  const now = new Date()
  const twoDaysAgo = new Date().setDate(now.getDate() - 2)

  return Date.parse(user.registrationDate) > twoDaysAgo
}

// TODO - this stuff doesn't need a "user" passed to it ... move elsewhere ?
// cookies
export const setAuthCookies = ({id, token}) => {
  jsCookie.set(environment.cookieKeys.id, id)
  jsCookie.set(environment.cookieKeys.token, token)
}

export function setSurveyIdCookie ({id}) {
  if (environment.inBrowser) Cookie.set(environment.cookieKeys.surveyId, id)
}

function getAuthFromStorage ({baseAccessorKey, storage}) {
  return [
    storage.accessors[baseAccessorKey],
    ...[
      'login',
      'register',
    ].map(urlKey => app.createAccessor(storageKeys[baseAccessorKey], environment.urls[urlKey])),
  ]
  .map(storage.get)
  .find(Boolean)
}

// storage
export const storage = {
  accessors: {
    id: app.createAccessor(storageKeys.id),
    token: app.createAccessor(storageKeys.token),
    language: storageKeys.language,
    country: storageKeys.country,
  },

  clear (key) {
    if (key) {
      Storage.removeItem(key)
    } else {
      // TODO - before login, set for destination application, not for current app!
      // clear local storage for all applications
      app.appUrls().forEach((url) => {
        Storage.removeItem(app.createAccessor(storageKeys.id, url))
        Storage.removeItem(app.createAccessor(storageKeys.token, url))
      })
    }
  },

  getId () {
    return parseInt(getAuthFromStorage({baseAccessorKey: 'id', storage}), 10) || null
  },

  getToken () {
    return getAuthFromStorage({baseAccessorKey: 'token', storage})
  },

  getLocaleLanguage () {
    return Cookie.get(storage.accessors.language) || getLangByIsoCode('en_us')
  },

  // authentication information
  // (default to login or registration information if not set for current app)
  setAuth (baseUrl, id, token) {
    storage.setId(baseUrl, id)
    storage.setToken(baseUrl, token)
  },

  setId (baseUrl, id) {
    if (!id) return

    const acc = typeof baseUrl === 'string' ?
      app.createAccessor(storageKeys.id, baseUrl) :
      storage.accessors.id

    storage.set(acc, id)
  },

  setToken (baseUrl, accessToken) {
    if (!accessToken) return

    const acc = typeof baseUrl === 'string' ?
      app.createAccessor(storageKeys.token, baseUrl) :
      storage.accessors.token

    storage.set(acc, accessToken)
  },

  get (key) {
    return Storage.getItem(key)
  },

  set (key, value) {
    Storage.setItem(key, value)
  },
}

export const setAuthForUrls = (predicatesAndUrls, user) => {
  predicatesAndUrls.filter(([predicate]) => predicate === true || predicate(user))
  .forEach(([, url]) => {
    storage.setAuth(url, user.id, user.accessToken)
  })
}

export const isProjectManager = () => {
  const tokenDecoded = parseJwt(Storage.getItem('accessToken'))
  return  (tokenDecoded && tokenDecoded['cognito:groups'].includes('QUAL_SCHEDULER_MANAGER')) 
}

export const isQSAdmin = () => {
  const tokenDecoded = parseJwt(Storage.getItem('accessToken'))
  return  (tokenDecoded && tokenDecoded['cognito:groups'].includes('QUAL_SCHEDULER_ADMIN')) 
}

export const isPMOrAdmin = () => {
  const tokenDecoded = parseJwt(Storage.getItem('accessToken'))
  return  (tokenDecoded && (tokenDecoded['cognito:groups'].includes('QUAL_SCHEDULER_ADMIN') || tokenDecoded['cognito:groups'].includes('QUAL_SCHEDULER_MANAGER'))) 
}

export const isModerator = () => {
  const tokenDecoded = parseJwt(Storage.getItem('accessToken'))
  return (tokenDecoded && tokenDecoded['cognito:groups'].includes('QUAL_SCHEDULER_MODERATOR'))
}

export const timezone = new Map
([["America/New_York","Eastern Time - New York"], 
["America/Toronto","Eastern Time - Canada"], 
["America/Chicago", "Central Time - Chicago"], 
["America/Winnipeg", " Central Time - Canada"], 
["America/Edmonton", "Mountain-Canada"], 
["America/Denver", "Mountain Time - Denver"], 
["America/Vancouver",  "Pacific Time - Canada"],
["America/Los_Angeles", "Pacific Time - Los Angeles"], 
["America/Anchorage", "Alaska Time - Anchorage"], 
["Pacific/Honolulu", "Hawaii Standard Time"],
["Asia/Beirut",  "Eastern European Time"], 
["Europe/Berlin",  "Central European Time"], 
["Europe/London",  "Western European Time"], 
["Africa/Abidjan",  "Greenwich Mean Time"], 
["America/Noronha",  "Fernando de Noronha Standard Time"], 
["America/Sao_Paulo",  "Brasilia Standard Time"], 
["America/Manaus",  "Amazon Standard Time"], 
["America/Rio_Branco",  "Acre Standard Time"]] )


export const timezoneAbbreviation = new Map
([["America/Noronha",  "FNT"], 
["America/Sao_Paulo",  "BRT"], 
["America/Manaus",  "AMT"], 
["America/Rio_Branco",  "ACT"]] )

// timezoneAbbreviationLabel was added to avoid having -02,-03,-04,-05 as abbreviations for the time zones listed in timezoneAbbreviation
export const timezoneAbbreviationLabel = (userTimezone) => timezoneAbbreviation.get(userTimezone) ? timezoneAbbreviation.get(userTimezone) : moment.tz(userTimezone).zoneAbbr()
